
    // Browsers without CSS anchor positioning get the listbox placed
    // here instead, by the same rule: below the input unless only the
    // space above fits it.
    const ANCHORED = typeof CSS !== "undefined" && CSS.supports("position-area: bottom")
    const QUERY_DEBOUNCE_MS = 300

    export default {
      mounted() {
        this.input = this.el.querySelector('input[role="combobox"]')
        this.anchor = this.el.querySelector("[data-picker-anchor]")
        this.focusedIdx = -1
        this._optSig = ""
        this._onKey = (e) => this.handleKey(e)
        this.input.addEventListener("keydown", this._onKey)

        // The query goes to the server from here, not through a form
        // `phx-change`: LiveView refuses that outside a form, and inside
        // a host form it would also fire the host's own `phx-change`.
        // Stopping the input's events keeps them out of both.
        this._onInput = (e) => {
          e.stopPropagation()
          if (!e.isComposing) this.queueQuery()
        }
        this._onChange = (e) => e.stopPropagation()
        this._onCompositionEnd = () => this.queueQuery()
        this.input.addEventListener("input", this._onInput)
        this.input.addEventListener("change", this._onChange)
        this.input.addEventListener("compositionend", this._onCompositionEnd)

        // Back in a field whose last search was skipped because the
        // user had left it: search what it holds now.
        this._onFocus = () => {
          if (this._skipped) this.queueQuery()
        }
        this.input.addEventListener("focus", this._onFocus)

        // Tabbing to another field leaves no click for phx-click-away,
        // and the list floats above the page, over the next row.
        this._onFocusOut = (e) => {
          if (e.relatedTarget && !this.el.contains(e.relatedTarget)) this.close()
        }
        this.el.addEventListener("focusout", this._onFocusOut)

        // A press on an option picks from the list on screen, as Enter
        // does; it also blurs the input, which would skip the search.
        this._onPointerDown = (e) => {
          if (e.target instanceof Element && e.target.closest('li[role="option"]')) this.cancelQuery()
        }
        this.el.addEventListener("pointerdown", this._onPointerDown)

        this._onViewportChange = (e) => {
          const list = this.listbox()
          if (!list || (e.target instanceof Node && list.contains(e.target))) return
          if (this._frame) return
          this._frame = requestAnimationFrame(() => {
            this._frame = null
            this.syncListbox({refit: true})
          })
        }
        window.addEventListener("scroll", this._onViewportChange, true)
        window.addEventListener("resize", this._onViewportChange)
        this.syncListbox()
      },

      listbox() {
        return this.el.querySelector("[data-picker-listbox]")
      },

      queueQuery() {
        clearTimeout(this._queryTimer)
        this._queryTimer = setTimeout(() => this.pushQuery(), QUERY_DEBOUNCE_MS)
      },

      // A search that lands after the user left the field would open
      // the list again under whatever they are doing now.
      pushQuery() {
        this._queryTimer = null
        this._skipped = document.activeElement !== this.input
        if (!this._skipped) this.pushEventTo(this.el, "query_change", {value: this.input.value})
      },

      cancelQuery() {
        clearTimeout(this._queryTimer)
        this._queryTimer = null
        this._skipped = false
      },

      close() {
        this.cancelQuery()
        this.pushEventTo(this.el, "close", {})
      },

      // The server renders the listbox only while open; each render of
      // it is a new element to show. With `refit`, an open list that no
      // longer fits on screen (the page scrolled under it, or new
      // results made it taller) is reopened: browsers pick a position
      // option when a popover opens, not when its anchor scrolls.
      syncListbox({refit = false} = {}) {
        const list = this.listbox()
        if (!list || typeof list.showPopover !== "function") {
          if (list && !ANCHORED) this.placeListbox(list)
          return
        }
        if (!list.matches(":popover-open")) {
          try { list.showPopover() } catch (_e) {}
        } else if (refit && ANCHORED && !this.fitsViewport(list)) {
          list.hidePopover()
          list.showPopover()
        }
        if (!ANCHORED) this.placeListbox(list)
      },

      // A pixel of slack: sub-pixel sizes must not reopen on every frame.
      fitsViewport(el) {
        const rect = el.getBoundingClientRect()
        return rect.top >= -1 && rect.bottom <= window.innerHeight + 1
      },

      placeListbox(list) {
        const rect = this.anchor.getBoundingClientRect()
        const gap = 4
        Object.assign(list.style, {
          position: "fixed",
          inset: "auto",
          margin: "0",
          left: `${rect.left}px`,
          width: `${rect.width}px`
        })
        const height = list.offsetHeight
        const below = window.innerHeight - rect.bottom - gap
        const above = rect.top - gap
        const top = height <= below || below >= above ? rect.bottom + gap : rect.top - gap - height
        list.style.top = `${Math.max(0, Math.min(top, window.innerHeight - height))}px`
      },

      updated() {
        // A NEW option set (debounced search landed) resets the
        // highlight — carrying the old index over highlights whatever
        // now sits at that position, and Enter would activate an item
        // the user never focused. Same-set re-renders only clamp.
        // Signature is by uuid: option ids are positional and identical
        // across different result sets of the same length.
        const opts = this.enabledOptions()
        const sig = opts.map((o) => o.getAttribute("phx-value-uuid")).join(" ")
        if (sig !== this._optSig) {
          this._optSig = sig
          this.focusedIdx = -1
        } else if (this.focusedIdx >= opts.length) {
          this.focusedIdx = opts.length - 1
        }
        this.syncActiveDescendant()
        this.syncListbox({refit: true})
      },

      destroyed() {
        this.cancelQuery()
        cancelAnimationFrame(this._frame)
        if (this.input && this._onKey) {
          this.input.removeEventListener("keydown", this._onKey)
          this.input.removeEventListener("input", this._onInput)
          this.input.removeEventListener("change", this._onChange)
          this.input.removeEventListener("compositionend", this._onCompositionEnd)
          this.input.removeEventListener("focus", this._onFocus)
        }
        this.el.removeEventListener("focusout", this._onFocusOut)
        this.el.removeEventListener("pointerdown", this._onPointerDown)
        window.removeEventListener("scroll", this._onViewportChange, true)
        window.removeEventListener("resize", this._onViewportChange)
      },

      enabledOptions() {
        return Array.from(
          this.el.querySelectorAll('li[role="option"]:not([aria-disabled="true"])')
        )
      },

      handleKey(e) {
        const opts = this.enabledOptions()

        switch (e.key) {
          case "ArrowDown":
            if (opts.length === 0) return
            e.preventDefault()
            this.focusedIdx = Math.min(this.focusedIdx + 1, opts.length - 1)
            if (this.focusedIdx < 0) this.focusedIdx = 0
            this.syncActiveDescendant()
            break

          case "ArrowUp":
            if (opts.length === 0) return
            e.preventDefault()
            this.focusedIdx = Math.max(this.focusedIdx - 1, 0)
            this.syncActiveDescendant()
            break

          case "Home":
            if (opts.length === 0) return
            e.preventDefault()
            this.focusedIdx = 0
            this.syncActiveDescendant()
            break

          case "End":
            if (opts.length === 0) return
            e.preventDefault()
            this.focusedIdx = opts.length - 1
            this.syncActiveDescendant()
            break

          case "Enter":
            if (this.focusedIdx >= 0 && this.focusedIdx < opts.length) {
              e.preventDefault()
              // The pick is from the list on screen; a search still
              // pending would reopen it over the chosen item.
              this.cancelQuery()
              opts[this.focusedIdx].click()
            }
            break

          case "Escape":
            e.preventDefault()
            this.close()
            break
        }
      },

      syncActiveDescendant() {
        this.el
          .querySelectorAll('li[data-focused="true"]')
          .forEach((el) => el.removeAttribute("data-focused"))

        const opts = this.enabledOptions()
        if (this.focusedIdx >= 0 && this.focusedIdx < opts.length) {
          const el = opts[this.focusedIdx]
          el.setAttribute("data-focused", "true")
          el.scrollIntoView({block: "nearest"})
          this.input.setAttribute("aria-activedescendant", el.id)
        } else {
          this.input.removeAttribute("aria-activedescendant")
        }
      }
    }
  